/**
 * Error utilities: normalize API errors and show consistent error toasts
 */

import { toast } from "react-hot-toast";
import { t } from "i18next";

export interface ApiErrorLike {
  status?: number;
  code?: string;
  message?: string;
  detail?: string;
  details?: unknown;
  errors?: Array<{ message?: string; field?: string } | string>;
  path?: string;
  raw?: unknown;
}

export function normalizeApiErrorFromResponse(
  response: Response,
  body: any
): ApiErrorLike {
  const tryMessageCandidates: Array<string | undefined> = [];

  if (body && typeof body === "object") {
    tryMessageCandidates.push(
      body.message,
      body.error,
      body.title,
      body.detail,
      (Array.isArray(body.errors) && body.errors.length > 0)
        ? (typeof body.errors[0] === "string" ? body.errors[0] : (body.errors[0] as any)?.message)
        : undefined
    );
  } else if (typeof body === "string") {
    tryMessageCandidates.push(body);
  }

  tryMessageCandidates.push(response.statusText);

  const message = (tryMessageCandidates.find(candidate => candidate && candidate.trim() !== "") as string | undefined) ??
    t("common:error:default", "Une erreur est survenue");

  const normalized: ApiErrorLike = {
    status: response.status,
    code: (body && (body.code || body.errorCode)) || undefined,
    message,
    detail: body?.detail,
    details: body?.details ?? body?.errors,
    path: (body && (body.path || body.endpoint)) || undefined,
    raw: body ?? undefined,
  };

  return normalized;
}

export function normalizeUnknownError(error: unknown): ApiErrorLike {
  if (!error) {
    return { message: t("common:error:default", "Une erreur est survenue") };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  if (error instanceof Error) {
    return { message: error.message };
  }

  if (typeof error === "object") {
    const anyErr = error as Record<string, any>;
    const message = anyErr.message || anyErr.error || anyErr.title || anyErr.detail;
    return {
      status: anyErr.status,
      code: anyErr.code || anyErr.errorCode,
      message: message ?? t("common:error:default", "Une erreur est survenue"),
      detail: anyErr.detail,
      details: anyErr.details ?? anyErr.errors,
      path: anyErr.path || anyErr.endpoint,
      raw: error,
    };
  }

  return { message: t("common:error:default", "Une erreur est survenue") };
}

export function getErrorMessage(err: unknown): string {
  const normalized = normalizeUnknownError(err);
  const message = normalized.message;

  // Si le backend renvoie une clé du type "error.xxx", on utilise le namespace error:xxx
  if (typeof message === "string" && message.startsWith("error.")) {
    const key = message.replace(/^error\./, "");
    const translated = t(`error:${key}`);
    if (translated && translated !== `error:${key}`) {
      return translated as unknown as string;
    }
  }

  return message || t("common:error:default", "Une erreur est survenue");
}

export function showApiErrorToast(
  err: unknown,
  options?: {
    // Texte de contexte, ex: "Erreur lors de la création"
    fallbackMessage?: string;
    // Clé i18n à utiliser comme préfixe (ex: "workorder:messages:createError")
    fallbackKey?: string;
    // Préfixe brut supplémentaire (rare)
    prefix?: string;
    duration?: number;
  }
): void {
  const baseMessage = getErrorMessage(err);

  // Préfixe utilisateur, priorités: fallbackKey (i18n) > fallbackMessage (brut) > prefix (brut)
  let prefixText = options?.prefix || "";
  if (options?.fallbackKey) {
    const translated = t(options.fallbackKey);
    if (translated && translated !== options.fallbackKey) {
      prefixText = translated as unknown as string;
    } else {
      // Si la clé est inconnue, on affiche la clé elle-même pour aider au debug
      prefixText = options.fallbackKey;
    }
  } else if (options?.fallbackMessage) {
    prefixText = options.fallbackMessage;
  }

  const parts = [prefixText?.trim(), baseMessage?.trim()].filter(Boolean);

  const { status, code } = (err as ApiErrorLike) || {};
  const codeOrStatus = code || status;
  const suffix = codeOrStatus ? ` (${codeOrStatus})` : "";

  toast.error(`${parts.join(" — ")}${suffix}`, {
    duration: options?.duration ?? 5000,
  });
}


